import { useState, useEffect, useRef } from 'react';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import TeamService from '@Helpers/URLHelpers/AuthService/TeamService';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import './SendInvitationModal.scss';

const isEmailLike = (str) => /^[^\s@]+@[^\s@]+/.test(str);

export default function SendInvitationModal(props) {
    const {
        open = false,
        onClose = () => {},
        existingUserIds = new Set(),
        pendingInviteeIds = new Set(),
        pendingInviteeEmails = new Set(),
        onInvitationSent = () => {},
        inviteTeamId = null,
        inviteTeamName = null,
    } = props;

    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [invitees, setInvitees] = useState([]);
    const [sending, setSending] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const searchTimeout = useRef(null);

    const userInfoService = new UserInfoService();
    const invitationService = new InvitationService();
    const orgService = new OrgService();
    const projectService = new ProjectService();
    const teamService = new TeamService();
    const errorHandler = new ErrorHandler();
    const { activeUserId } = useReduxActiveUser();
    const { currentProject } = useReduxProject();
    const { currentOrgId } = useReduxOrg();

    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        const trimmed = query.trim();
        if (!trimmed || trimmed.length < 3 || isEmailLike(trimmed)) {
            setSearchResults([]);
            setDropdownOpen(trimmed.length > 0);
            return;
        }

        setDropdownOpen(true);
        searchTimeout.current = setTimeout(() => performSearch(trimmed), 350);

        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
    }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

    const performSearch = async (searchQuery) => {
        try {
            setIsSearching(true);
            let usersData = await userInfoService.searchUserByKeyword(searchQuery.toLowerCase());
            usersData = JSON.parse(usersData.body);
            const currentUserId = typeof activeUserId === 'string' ? JSON.parse(activeUserId) : activeUserId;
            const results = (usersData || []).filter(user => Number(user.id) !== Number(currentUserId));
            setSearchResults(results);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setIsSearching(false);
        }
    };

    const getUserStatus = (user) => {
        const userId = Number(user.id);
        const emailLower = user.email?.toLowerCase();
        const queuedUserIds = new Set(invitees.filter(i => i.type === 'user').map(i => Number(i.user.id)));
        const queuedEmails = new Set(invitees.filter(i => i.type === 'email').map(i => i.email.toLowerCase()));
        if (existingUserIds.has(userId)) return 'member';
        if (pendingInviteeIds.has(userId)) return 'invited';
        if (emailLower && pendingInviteeEmails.has(emailLower)) return 'invited';
        if (queuedUserIds.has(userId)) return 'queued';
        if (emailLower && queuedEmails.has(emailLower)) return 'queued';
        return null;
    };

    const STATUS_LABEL = {
        member: 'Already a member',
        invited: 'Already invited',
        queued: 'Added',
    };

    const handleSelectUser = (user) => {
        const label = user.fullName || user.userName;
        setInvitees(prev => [...prev, { type: 'user', user, label }]);
        setDropdownOpen(false);
        setQuery('');
        setSearchResults([]);
    };

    const handleSelectEmail = () => {
        const email = query.trim();
        const emailLower = email.toLowerCase();
        if (invitees.some(i => i.type === 'email' && i.email.toLowerCase() === emailLower)) {
            SnackbarManager.show?.(`${email} is already in the invite list`, 'warning');
            return;
        }
        if (pendingInviteeEmails.has(emailLower)) {
            SnackbarManager.show?.(`An invitation to ${email} has already been sent`, 'warning');
            return;
        }
        setInvitees(prev => [...prev, { type: 'email', email, label: email }]);
        setDropdownOpen(false);
        setQuery('');
    };

    const handleRemovePill = (index, e) => {
        e.stopPropagation();
        setInvitees(prev => prev.filter((_, i) => i !== index));
    };

    const handleClose = () => {
        if (invitees.length > 0) {
            setShowConfirmClose(true);
        } else {
            resetModal();
            onClose();
        }
    };

    const resetModal = () => {
        setQuery('');
        setSearchResults([]);
        setIsSearching(false);
        setDropdownOpen(false);
        setInvitees([]);
        setSending(false);
        setShowConfirmClose(false);
    };

    const handleSendAll = async () => {
        if (invitees.length === 0) return;
        try {
            setSending(true);
            const inviterId = typeof activeUserId === 'string' ? JSON.parse(activeUserId) : activeUserId;

            for (const invitee of invitees) {
                if (invitee.type === 'email') {
                    await invitationService.createInvitation({
                        orgId: Number(currentOrgId),
                        teamId: inviteTeamId ? Number(inviteTeamId) : 0,
                        projectId: currentProject?.id,
                        parentType: 'project',
                        parentId: currentProject?.id,
                        inviterId: Number(inviterId),
                        invitationParentVisibilityType: 'resolve',
                        permissions: ['agent'],
                        inviteeEmail: invitee.email,
                    });
                } else {
                    const userId = invitee.user?.id;
                    const alreadyInProject = existingUserIds.has(Number(userId));

                    if (inviteTeamId) {
                        if (alreadyInProject) {
                            await teamService.addContributorToTeam({
                                childType: 'user',
                                userId,
                                teamId: Number(inviteTeamId),
                                permissions: JSON.stringify(['read', 'write']),
                            });
                        } else {
                            await orgService.addUserToOrg({
                                orgId: Number(currentOrgId),
                                permissions: JSON.stringify(['admin']),
                                userId,
                            });
                            await projectService.addContributorToProject({
                                childType: 'user',
                                userId,
                                projectId: currentProject?.id,
                                permissions: JSON.stringify(['admin']),
                            });
                            await teamService.addContributorToTeam({
                                childType: 'user',
                                userId,
                                teamId: Number(inviteTeamId),
                                permissions: JSON.stringify(['read', 'write']),
                            });
                        }
                    } else {
                        await orgService.addUserToOrg({
                            orgId: Number(currentOrgId),
                            permissions: JSON.stringify(['admin']),
                            userId,
                        });
                        await projectService.addContributorToProject({
                            childType: 'user',
                            userId,
                            projectId: currentProject?.id,
                            permissions: JSON.stringify(['admin']),
                        });
                    }
                }
            }

            const userCount = invitees.filter(i => i.type === 'user').length;
            const emailCount = invitees.filter(i => i.type === 'email').length;
            const parts = [];
            if (userCount > 0) parts.push(`${userCount} user${userCount !== 1 ? 's' : ''} added`);
            if (emailCount > 0) parts.push(`${emailCount} invitation${emailCount !== 1 ? 's' : ''} sent`);
            SnackbarManager.show?.(parts.join(', '), 'success');

            onInvitationSent(invitees);
            resetModal();
            onClose();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to process some users', 'error');
        } finally {
            setSending(false);
        }
    };

    const isEmail = isEmailLike(query.trim());
    const trimmedQuery = query.trim();
    const showDropdown = dropdownOpen && trimmedQuery.length > 0;
    const showHint = !isEmail && trimmedQuery.length > 0 && trimmedQuery.length < 3;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                className="sendInvitationModal"
                PaperProps={{ className: 'sendInvitationModal__paper' }}
            >
                <DialogTitle className="sendInvitationModal__title">
                    <div className="sendInvitationModal__header">
                        <div className="sendInvitationModal__titleRow">
                            <span className="sendInvitationModal__titleText">
                                {inviteTeamName ? `Invite to ${inviteTeamName}` : 'Invite Collaborators'}
                            </span>
                            {invitees.length > 0 && (
                                <span className="sendInvitationModal__titleBadge">
                                    {invitees.length} queued
                                </span>
                            )}
                        </div>
                    </div>
                </DialogTitle>

                <DialogContent className="sendInvitationModal__content">
                    <p className="sendInvitationModal__subtitle">
                        {inviteTeamName
                            ? `Search for someone to invite to the ${inviteTeamName} team, or type an email address.`
                            : 'Search for a team member by name, or type an email address to send an external invitation.'}
                    </p>

                    {invitees.length > 0 && (
                        <div className="sendInvitationModal__pillsSection">
                            <div className="sendInvitationModal__pillsLabel">
                                To be added
                            </div>
                            <div className="sendInvitationModal__pills">
                                {invitees.map((invitee, index) => (
                                    <div
                                        key={index}
                                        className="sendInvitationModal__pill"
                                    >
                                        <div className="sendInvitationModal__pill__avatar">
                                            {invitee.type === 'user'
                                                ? (invitee.label || 'U').slice(0, 1).toUpperCase()
                                                : '@'}
                                        </div>
                                        <div className="sendInvitationModal__pill__info">
                                            <span className="sendInvitationModal__pill__name">{invitee.label}</span>
                                            {invitee.type === 'email' && (
                                                <span className="sendInvitationModal__pill__tag">External</span>
                                            )}
                                        </div>
                                        <button
                                            className="sendInvitationModal__pill__remove"
                                            onClick={(e) => handleRemovePill(index, e)}
                                            title="Remove"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="sendInvitationModal__searchArea">
                        <div className="sendInvitationModal__inputWrapper">
                            <TextField
                                placeholder="Search by name, or type an email address..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                variant="outlined"
                                fullWidth
                                size="small"
                                autoComplete="off"
                                className="sendInvitationModal__input"
                                InputProps={{
                                    endAdornment: isSearching
                                        ? <CircularProgress size={14} style={{ color: 'var(--color-primaryGrey)', flexShrink: 0 }} />
                                        : null,
                                }}
                            />
                            {showHint && (
                                <div className="sendInvitationModal__inputHint">
                                    Type {3 - trimmedQuery.length} more character{3 - trimmedQuery.length !== 1 ? 's' : ''} to search
                                </div>
                            )}
                        </div>

                        {showDropdown && (
                            <div className="sendInvitationModal__dropdown">
                                {isEmail ? (() => {
                                    const emailLower = trimmedQuery.toLowerCase();
                                    const alreadyInvited = pendingInviteeEmails.has(emailLower);
                                    const alreadyQueued = invitees.some(i => i.type === 'email' && i.email.toLowerCase() === emailLower);
                                    const emailDisabled = alreadyInvited || alreadyQueued;
                                    const emailStatusLabel = alreadyInvited ? 'Already invited' : alreadyQueued ? 'Added' : null;
                                    return (
                                        <button
                                            className={`sendInvitationModal__emailOption${emailDisabled ? ' disabled' : ''}`}
                                            onClick={emailDisabled ? undefined : handleSelectEmail}
                                            type="button"
                                            disabled={emailDisabled}
                                        >
                                            <span className="sendInvitationModal__emailOption__icon">@</span>
                                            <span>Send invitation to <strong>{trimmedQuery}</strong></span>
                                            {emailStatusLabel && (
                                                <span className={`sendInvitationModal__dropdownItem__statusTag sendInvitationModal__dropdownItem__statusTag--${alreadyInvited ? 'invited' : 'queued'}`}>
                                                    {emailStatusLabel}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })() : (
                                    <>
                                        {isSearching && (
                                            <div className="sendInvitationModal__dropdownStatus">
                                                <CircularProgress size={13} />
                                                <span>Searching...</span>
                                            </div>
                                        )}
                                        {!isSearching && searchResults.length === 0 && (
                                            <div className="sendInvitationModal__dropdownStatus">
                                                No users found — try an email address to send an external invite
                                            </div>
                                        )}
                                        {!isSearching && searchResults.map(user => {
                                            const status = getUserStatus(user);
                                            const isDisabled = !!status;
                                            return (
                                                <button
                                                    key={user.id}
                                                    className={`sendInvitationModal__dropdownItem${isDisabled ? ' disabled' : ''}`}
                                                    onClick={() => !isDisabled && handleSelectUser(user)}
                                                    type="button"
                                                    disabled={isDisabled}
                                                >
                                                    <div className="sendInvitationModal__dropdownItem__avatar">
                                                        {(user.fullName || user.userName || 'U').slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <div className="sendInvitationModal__dropdownItem__info">
                                                        <div className="sendInvitationModal__dropdownItem__name">
                                                            {user.fullName || user.userName}
                                                        </div>
                                                        <div className="sendInvitationModal__dropdownItem__email">
                                                            {user.email || user.userName}
                                                        </div>
                                                    </div>
                                                    {isDisabled && (
                                                        <span className={`sendInvitationModal__dropdownItem__statusTag sendInvitationModal__dropdownItem__statusTag--${status}`}>
                                                            {STATUS_LABEL[status]}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>

                <DialogActions className="sendInvitationModal__actions">
                    <Button onClick={handleClose} variant="text" disabled={sending}>
                        Close
                    </Button>
                    <Button
                        onClick={handleSendAll}
                        variant="contained"
                        disabled={invitees.length === 0 || sending}
                    >
                        {sending
                            ? 'Processing...'
                            : invitees.length === 0
                                ? 'Add / Invite'
                                : invitees.every(i => i.type === 'user')
                                    ? `Add ${invitees.length} User${invitees.length !== 1 ? 's' : ''}`
                                    : invitees.every(i => i.type === 'email')
                                        ? `Send ${invitees.length} Invitation${invitees.length !== 1 ? 's' : ''}`
                                        : `Add & Invite (${invitees.length})`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showConfirmClose}
                onClose={() => setShowConfirmClose(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ className: 'sendInvitationModal__confirmPaper' }}
            >
                <DialogTitle className="sendInvitationModal__confirmTitle">
                    Discard invitations?
                </DialogTitle>
                <DialogContent>
                    <p className="sendInvitationModal__confirmText">
                        You have <strong>{invitees.length}</strong> pending invitation{invitees.length !== 1 ? 's' : ''} that haven't been sent. Closing now will discard them.
                    </p>
                </DialogContent>
                <DialogActions className="sendInvitationModal__confirmActions">
                    <Button onClick={() => setShowConfirmClose(false)} variant="text">
                        Keep editing
                    </Button>
                    <Button
                        onClick={() => { resetModal(); onClose(); }}
                        variant="contained"
                        className="sendInvitationModal__discardBtn"
                    >
                        Discard & Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
